import { useCallback, useEffect, useState } from "react";
import { atom, useAtom } from "jotai";
import useIsMobile from "@/hooks/common/useIsMobile";

export const intersectingNumberAtom = atom<number>(-1);
export const employmentIntersectingNumberAtom = atom<number>(-1);

export const useIntersection = (
  elemRef: { current: Element | null },
  callback,
  deps: boolean[],
  threshold: number[],
  rootMargin = "0px"
) => {
  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0];
      if (target.isIntersecting && !deps[0]) {
        callback();
      }
    },
    [...deps]
  );

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: rootMargin,
      threshold: threshold,
    };
    const observer = new IntersectionObserver(handleObserver, option);
    if (elemRef.current) {
      observer.observe(elemRef.current);
    }
    return () => observer.disconnect();
  }, [elemRef, handleObserver, rootMargin]);
};

export const useVisible = (
  elemRef: { current: Element | null },
  visibleHandler: () => void = () => {},
  invisibleHandler: () => void = () => {},
  fireOnOut = false,
  rootMargin = "-49% 0px -50% 0px"
) => {
  const [, setIntersectingNumber] = useAtom(intersectingNumberAtom);
  const handleObserver = useCallback((entries) => {
    const target = entries[0];
    if (target.isIntersecting) {
      visibleHandler();
      setIntersectingNumber(+elemRef.current.id);
    }
  }, []);

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: rootMargin,
      threshold: [0],
    };
    const observer = new IntersectionObserver(handleObserver, option);
    if (elemRef.current) {
      observer.observe(elemRef.current);
    }

    return () => observer.disconnect();
  }, [elemRef, handleObserver, rootMargin]);
};

export const useVisibleEmployment = (
  elemRef: { current: Element | null },
  isMobile: boolean,
  visibleHandler: () => void = () => {},
  invisibleHandler: () => void = () => {},
  fireOnOut = false,
  rootMargin = "-40% 0px -56% 0px"
) => {
  const [, setIntersectingNumber] = useAtom(employmentIntersectingNumberAtom);
  const handleObserver = useCallback((entries) => {
    const target = entries[0];
    if (target.isIntersecting) {
      visibleHandler();
      setIntersectingNumber(+elemRef.current.id);
    }
  }, []);

  if (isMobile) {
    rootMargin = "-18% 0px -77% 0px";
  }

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: rootMargin,
      threshold: [0],
    };

    const observer = new IntersectionObserver(handleObserver, option);
    if (elemRef.current) {
      observer.observe(elemRef.current);
    }

    return () => observer.disconnect();
  }, [elemRef, handleObserver, rootMargin]);
};
